import {
    type TextStyle,
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    TabBarItemBadgeLocationConstantType,
    TabBarItemMeasuresConstantType,
    TabBarItemColorsConstantType,
    TabBarItemWeightConstantType,
    TabBarItemBadgeLocationType,
    TabBarItemDynamicStyleType,
    TabBarItemMeasures,
    TabBarItemColors
} from "./type";
import {
    getTypeColors
} from "../../../../helpers/theme/typeColors";
import type {
    Mutable
} from "../../../../types";
import {
    getFocusRingStyle
} from "../../../../helpers/theme/state";
import {
    getCornerStyle
} from "../../../../helpers/theme/corner";
import {
    webStyle
} from "../../../../utils";

export const TAB_BAR_ITEM_LABEL_VARIANT: keyof NCoreUIKit.Typography = "labelSmallSize";

export const TAB_BAR_ITEM_ACTIVE_WEIGHT_VARIANT: keyof NCoreUIKit.Typography = "titleSmallSize";

export const getTabBarItemColors = ({
    isDisabled,
    isActive,
    colors
}: TabBarItemColorsConstantType): TabBarItemColors => {
    const currentColors = getTypeColors({
        isSelected: isActive,
        variant: "ghost",
        type: "neutral",
        isDisabled,
        colors
    });

    if(isDisabled) {
        return {
            titleColor: currentColors.titleColor,
            iconColor: currentColors.iconColor,
            containerColor: "transparent",
            iconColorKey: "disabled"
        };
    }

    if(isActive) {
        return {
            containerColor: currentColors.containerColor,
            titleColor: currentColors.titleColor,
            iconColor: currentColors.iconColor,
            iconColorKey: "high"
        };
    }

    return {
        titleColor: colors.content.text.mid,
        iconColor: colors.content.icon.mid,
        containerColor: "transparent",
        iconColorKey: "mid"
    };
};

export const getTabBarItemMeasures = ({
    controlHeights,
    labelBehaviour,
    touchTargets,
    spaces
}: TabBarItemMeasuresConstantType): TabBarItemMeasures => {
    const indicatorHeight = controlHeights.small;

    const indicatorWidth = controlHeights.small * 2;

    if(labelBehaviour === "never") {
        return {
            minHeight: Math.max(touchTargets.preferred, indicatorHeight + spaces.spacingSm * 2),
            paddingVertical: 0,
            indicatorHeight,
            indicatorWidth
        };
    }

    return {
        minHeight: touchTargets.preferred,
        paddingVertical: spaces.spacingSm,
        indicatorHeight,
        indicatorWidth
    };
};

export const getTabBarItemBadgeLocation = ({
    iconSizes,
    badge
}: TabBarItemBadgeLocationConstantType): TabBarItemBadgeLocationType => {
    if(badge === "dot") {
        return {
            start: undefined,
            end: 0,
            top: 0
        };
    }

    return {
        start: iconSizes.large / 2,
        top: -iconSizes.large / 4
    };
};

export const getTabBarItemActiveWeight = ({
    typography
}: TabBarItemWeightConstantType): TextStyle["fontWeight"] => {
    return typography[TAB_BAR_ITEM_ACTIVE_WEIGHT_VARIANT].fontWeight as TextStyle["fontWeight"];
};

const stylesheet = StyleSheet.create({
    overlay: {
        ...StyleSheet.absoluteFill,
        pointerEvents: "none"
    },
    container: {
        alignItems: "center",
        flex: 1,
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    indicator: {
        justifyContent: "center",
        alignItems: "center",
        maxWidth: "100%"
    },
    label: {
        textAlign: "center",
        maxWidth: "100%"
    }
});

export const useStyles = ({
    controlHeights,
    isFocusVisible,
    labelBehaviour,
    shapeConfigs,
    touchTargets,
    isDisabled,
    typography,
    isActive,
    radiuses,
    borders,
    colors,
    spaces
}: TabBarItemDynamicStyleType) => {
    const measures = getTabBarItemMeasures({
        controlHeights,
        labelBehaviour,
        touchTargets,
        spaces
    });

    const styles = {
        placeholder: {
            height: typography[TAB_BAR_ITEM_LABEL_VARIANT].lineHeight
        },
        container: {
            paddingVertical: measures.paddingVertical,
            minWidth: touchTargets.preferred,
            minHeight: measures.minHeight,
            gap: spaces.spacingXs
        } as Mutable<ViewStyle>,
        indicator: {
            height: measures.indicatorHeight,
            ...getCornerStyle({
                radius: radiuses.actions,
                shapeConfigs
            }),
            width: measures.indicatorWidth
        } as Mutable<ViewStyle>,
        label: {
        } as Mutable<TextStyle>
    };

    if(labelBehaviour === "never") {
        styles.container.justifyContent = "center";
    }

    if(isActive && !isDisabled) {
        styles.label.fontWeight = getTabBarItemActiveWeight({
            typography
        });
    }

    if(isDisabled) {
        Object.assign(styles.container, webStyle({
            cursor: "not-allowed"
        }));
    }

    Object.assign(styles.indicator, getFocusRingStyle({
        colorKey: "default",
        isFocusVisible,
        borders,
        colors
    }));

    return styles;
};
export default stylesheet;
